/**
 * L'accueil du masteur, « Ma journée » : ce que `<TutorHomePage>` montre, lu dans le store
 * (patron A : chaque réponse a son réducteur).
 *
 * Port de `TutorHomeTabScreen.js` réordonné (la piste A1) — chaque pièce a son adaptateur, qui cite
 * l'ancien composant : les lives du jour (`tutorSessionAdapter`, l'ancienne « Mes prochains lives »,
 * lue sur `currentUser.live_sessions`), « À faire » (`tutorTodoAdapter`, la bannière des tâches et la
 * formation, lues sur `tutors.tasks`), l'offre en attente (`tutorOfferAdapter`, lue sur les listes que
 * l'onglet « Offres de cours » a rangées dans le compte : l'accueil n'en lance aucune, voir
 * `useTutorHomeData`), « Mon score » et « Nouveau mois » (`tutorScoreAdapter`, `tutorMonthlyScore`),
 * « Mon mois » (`tutorEarningsAdapter`, les statistiques du compte).
 *
 * Qui voit quoi, comme l'ancien écran : « À faire » sous le droit `my_tutor_profile_completion`,
 * le reste sous `my_students` (sinon « Bientôt disponible ») ; les droits viennent du contexte que
 * l'écran lit et passe. Un `createSelector` sur les tranches qu'il lit (`ports/CLAUDE.md`).
 */
import moment from "moment"
import { createSelector } from "reselect"

import { toUITutorEarnings, type ApiTutorEarningsUser } from "../../../adapters/tutor/Home/tutorEarningsAdapter"
import {
    toUITutorMonthReset,
    toUITutorScore,
    type ApiTutorMonthlyScoreState,
} from "../../../adapters/tutor/Home/tutorScoreAdapter"
import {
    toTutorHomeSessions,
    type ApiTutorLiveSession,
    type UITutorHomeSession,
} from "../../../adapters/tutor/Home/tutorSessionAdapter"
import {
    toUITutorTodos,
    tutorAvailabilityNudge,
    type ApiTutorTasks,
} from "../../../adapters/tutor/Home/tutorTodoAdapter"
import { tutorHomeOffer, type ApiTutorOffersUser } from "../../../adapters/tutor/shared/tutorOfferAdapter"
import { type ApiSchoolSubject } from "../../../adapters/parent/shared/schoolSubjectAdapter"
import {
    type UITutorEarnings,
    type UITutorMonthReset,
    type UITutorScore,
    type UITutorTodo,
} from "../../../ui/types/tutorCoquilleAccueil"
import { type UITutorOffer } from "../../../ui/types/tutorDispoOffres"
import { selectAccount } from "../../shared/displayedStudent"

export type TutorHomeRights = {
    /** `read my_students` : les lives, l'offre, le score et le mois. */
    canSeeStudents: boolean
    /** `read my_tutor_profile_completion` : la bannière des tâches. */
    canSeeTasks: boolean
}

export type TutorHomeViewModel = {
    firstname?: string
    /** Le compte n'est pas encore là : le squelette (l'ancienne roue plein écran). */
    loading: boolean
    canSeeStudents: boolean
    todaySessions: UITutorHomeSession[]
    upcomingSessions: UITutorHomeSession[]
    todos: UITutorTodo[]
    offer: UITutorOffer | null
    pendingOffersCount: number
    score: UITutorScore | null
    earnings: UITutorEarnings | null
    monthReset: UITutorMonthReset | null
    /** L'e-mail du compte : la demande du formulaire Stripe le prend. */
    email?: string
    /** La semaine-type est à confirmer (`availabilityNudgeFromState`) : le rappel mensuel de « Ma dispo » peut s'ouvrir. */
    availabilityNudge: boolean
}

type Account =
    | (ApiTutorEarningsUser &
          ApiTutorOffersUser & {
              firstname?: string | null
              email?: string | null
              live_sessions?: ApiTutorLiveSession[] | null
              profile?: { availability_freshness?: string | null } | null
          })
    | null

type HomeState = {
    data?: {
        tutors?: { tasks?: ApiTutorTasks | null } | null
        tutorMonthlyScore?: ApiTutorMonthlyScoreState | null
        staticData?: { school_subjects?: ApiSchoolSubject[] | null; monthly_activity_threshold?: number | null } | null
    }
}

const root = (state: unknown) => state as HomeState

const selectHome = createSelector(
    [
        (state: unknown) => selectAccount(state as never) as Account,
        (state: unknown) => root(state)?.data?.tutors?.tasks ?? null,
        (state: unknown) => root(state)?.data?.tutorMonthlyScore ?? null,
        (state: unknown) => root(state)?.data?.staticData?.school_subjects ?? null,
        (state: unknown) => root(state)?.data?.staticData?.monthly_activity_threshold ?? null,
        (_state: unknown, rights: TutorHomeRights) => rights.canSeeStudents,
        (_state: unknown, rights: TutorHomeRights) => rights.canSeeTasks,
        // Le jour : « Aujourd'hui » se recalcule passé minuit, à la première action du store.
        () => new Date().toDateString(),
    ],
    (account, tasks, scores, schoolSubjects, threshold, canSeeStudents, canSeeTasks): TutorHomeViewModel => {
        const sessions = toTutorHomeSessions(account?.live_sessions, schoolSubjects, moment())
        const { offer, pendingCount } = tutorHomeOffer(account)
        return {
            firstname: account?.firstname ?? undefined,
            loading: account == null,
            canSeeStudents,
            todaySessions: sessions.today,
            upcomingSessions: sessions.upcoming,
            // L'ancien écran : la bannière sous le droit, la formation pour le rôle `tutor` (la coquille
            // ne s'ouvre qu'à lui).
            todos: toUITutorTodos(tasks, { showTasks: canSeeTasks && account != null, showTraining: true }),
            offer: canSeeStudents ? offer : null,
            pendingOffersCount: pendingCount,
            score: canSeeStudents ? toUITutorScore(scores, threshold) : null,
            earnings: canSeeStudents ? toUITutorEarnings(account) : null,
            monthReset: toUITutorMonthReset(scores),
            email: account?.email ?? undefined,
            availabilityNudge: tutorAvailabilityNudge(tasks, account?.profile?.availability_freshness),
        }
    }
)

export function tutorHomeViewModel(state: unknown, rights: TutorHomeRights): TutorHomeViewModel {
    return selectHome(state, rights)
}
